import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "../lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [loading, setLoading] = useState(true);
  const [account, setAccount] = useState(null);
  const [workspaces, setWorkspaces] = useState([]);
  const [lastOrgId, setLastOrgId] = useState(null);

  const applyPayload = useCallback((data) => {
    localStorage.setItem("bp_auth_cache", JSON.stringify({
      account: data.account, workspaces: data.workspaces,
      last_workspace_org_id: data.last_workspace_org_id,
    }));
    setAccount(data.account);
    setWorkspaces(data.workspaces || []);
    setLastOrgId(data.last_workspace_org_id || data.workspaces?.[0]?.org_id || null);
    if (data.account?.preferred_language) {
      window.dispatchEvent(new CustomEvent("bp:lang", { detail: data.account.preferred_language }));
    }
    if (data.account?.theme_preference) {
      window.dispatchEvent(new CustomEvent("bp:theme", { detail: data.account.theme_preference }));
    }
  }, []);

  useEffect(() => {
    api
      .get("/identity/me")
      .then(({ data }) => applyPayload(data))
      .catch((err) => {
        if (err.response) {
          localStorage.removeItem("bp_auth_cache");
        } else {
          const cached = localStorage.getItem("bp_auth_cache");
          if (cached) {
            try { applyPayload(JSON.parse(cached)); } catch (e) { console.warn("bp_auth_cache corrupt", e); }
          }
        }
      })
      .finally(() => setLoading(false));
  }, [applyPayload]);

  const login = useCallback(
    async (phone, password) => {
      const { data } = await api.post("/identity/auth/login", { phone, password });
      applyPayload(data);
      return data;
    },
    [applyPayload]
  );

  const register = useCallback(
    async (body) => {
      const { data } = await api.post("/identity/auth/register", body);
      applyPayload(data);
      return data;
    },
    [applyPayload]
  );

  const logout = useCallback(() => {
    api.post("/identity/auth/logout").catch((e) => console.warn("logout request failed", e));
    localStorage.removeItem("bp_auth_cache");
    setAccount(null);
    setWorkspaces([]);
    setLastOrgId(null);
  }, []);

  const switchWorkspace = useCallback(async (orgId) => {
    await api.post("/identity/me/workspace", { org_id: orgId });
    setLastOrgId(orgId);
  }, []);

  const acceptInvite = useCallback(async (code) => {
    const { data } = await api.post("/identity/invites/accept", { code });
    setWorkspaces(data.workspaces || []);
    return data;
  }, []);

  const refresh = useCallback(async () => {
    const { data } = await api.get("/identity/me");
    applyPayload(data);
  }, [applyPayload]);

  const value = {
    loading,
    account,
    workspaces,
    lastOrgId,
    login,
    register,
    logout,
    switchWorkspace,
    acceptInvite,
    refresh,
  };
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  return useContext(AuthContext);
}

export function useWorkspace(orgType) {
  const { workspaces, lastOrgId } = useAuth();
  const ofType = workspaces.filter((w) => w.org_type === orgType);
  return ofType.find((w) => w.org_id === lastOrgId) || ofType[0] || null;
}
